import { useEffect, useState } from 'react';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useRefWithCurrentValue } from 'record-it-components/hooks/useRefWithCurrentValue';
import { AsyncQueue } from 'record-it-utils/queue/AsyncQueue/AsyncQueue';
export function useInitializable<T extends Initializable>({
  createInstance
}: UseInitializableOptions<T>): UseInitializableResult<T> {
  const [initializable, setInitializable] = useState<T | null>(null);
  const initializableRef = useRefWithCurrentValue(initializable);
  const [queue] = useState(new AsyncQueue());
  const createNewInstance = useEventCallback<() => Promise<void>>(() => {
    return new Promise((resolve, reject) => {
      // we need to remove the old instance before creating a new one
      queueRemoveInstanceWorker(() => {}, () => {});
      queueCreateNewInstanceWorker(resolve, reject);
    });
  });
  const queueCreateNewInstanceWorker = useEventCallback((resolve_0: () => void, reject_0: (e: unknown) => void): void => {
    queue.add(async () => {
      try {
        const newInitializable = createInstance();
        await newInitializable.init();
        setInitializable(newInitializable);
        resolve_0();
      } catch (e) {
        reject_0(e);
      }
    });
  });
  const removeInstance = useEventCallback(() => {
    return new Promise<void>((resolve_1, reject_1) => {
      queueRemoveInstanceWorker(resolve_1, reject_1);
    });
  });
  const queueRemoveInstanceWorker = useEventCallback((resolve_2: () => void, reject_2: (e: unknown) => void): void => {
    queue.add(async () => {
      try {
        // we need to access the ref here to get the current instance
        // if we access the `initializable` here, we access the initializable at the the we queued this function.
        if (initializableRef.current) {
          await initializableRef.current.flush?.();
          initializableRef.current.destroy?.();
        }
        setInitializable(null);
        resolve_2();
      } catch (e_0) {
        reject_2(e_0);
      }
    });
  });
  useEffect(() => {
    queueCreateNewInstanceWorker(() => {}, e_1 => {
      throw e_1;
    });
    return () => {
      queueRemoveInstanceWorker(() => {}, e_2 => {
        throw e_2;
      });
    };
  }, [queueCreateNewInstanceWorker, queueRemoveInstanceWorker]);
  return {
    initializable,
    createNewInstance,
    removeInstance
  };
}
export type UseInitializableOptions<T extends Initializable> = {
  createInstance: () => T;
};
export type UseInitializableResult<T extends Initializable> = {
  initializable: T | null;
  removeInstance: () => Promise<void>;
  createNewInstance: () => Promise<void>;
};
export type Initializable = {
  init: () => Promise<void>;
  flush?: () => Promise<void>;
  destroy?: () => void;
};